import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook for intelligent auto-scroll functionality with typing detection
 * @param {Array} fieldOrder - Array of field names in order
 * @param {Function} isFieldValid - Function to validate if a field is valid
 * @returns {Object} Auto-scroll utilities
 */
export const useAutoScroll = (fieldOrder, isFieldValid) => {
  const [fieldValidationStates, setFieldValidationStates] = useState({});
  const [lastFocusedField, setLastFocusedField] = useState(null);
  
  // Track typing activity per field
  const typingTimers = useRef({});
  const activeFields = useRef(new Set()); // Fields currently being typed in
  const scrollTimers = useRef({});
  
  // Adaptive inactivity delays by field type (in milliseconds)
  const getInactivityDelay = useCallback((fieldName) => {
    const delayMap = {
      'registrationNumber': 2000,      // 2 seconds
      'emailAddress': 2000,            // 2 seconds
      'chassisNumber': 2000,           // 2 seconds
      'insuredName': 1800,             // 1.8 seconds
      'phoneNumber': 1500,             // 1.5 seconds
      'kraPin': 1500,                  // 1.5 seconds
      'idNumber': 1200                 // 1.2 seconds
    };
    return delayMap[fieldName] || 1000; // Default: 1 second
  }, []);

  // Initialize field validation states
  const initializeFieldStates = useCallback((fields) => {
    const initialStates = {};
    fields.forEach(field => {
      initialStates[field] = { isValid: false, isComplete: false, hasScrolled: false };
    });
    setFieldValidationStates(initialStates);
  }, []);

  // Smooth scroll to field function with faster animation
  const scrollToField = useCallback((fieldName) => {
    const fieldElement = document.getElementById(fieldName);
    if (fieldElement) {
      // Add visual highlight effect
      fieldElement.style.transition = 'all 0.2s ease';
      fieldElement.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.3)';
      
      // Fast smooth scroll to field using custom animation
      const elementRect = fieldElement.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const offset = window.innerHeight / 2 - elementRect.height / 2; // Center the element
      const targetPosition = absoluteElementTop - offset;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();
      const duration = 200; // Faster: 200ms instead of default ~500ms
      
      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth deceleration
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentPosition = startPosition + distance * easeOutCubic;
        
        window.scrollTo(0, currentPosition);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          // Focus the field after scroll completes
          setTimeout(() => {
            fieldElement.focus();
            // Remove highlight after focus
            setTimeout(() => {
              fieldElement.style.boxShadow = '';
            }, 800);
          }, 100);
        }
      };
      
      requestAnimationFrame(animateScroll);
    }
  }, []);

  // Check if all fields are complete and valid
  const areAllFieldsComplete = useCallback((currentFieldStates) => {
    return fieldOrder.every(field => {
      const fieldState = currentFieldStates[field];
      return fieldState && fieldState.isValid && fieldState.isComplete;
    });
  }, [fieldOrder]);

  // Scroll to continue button with faster animation
  const scrollToContinueButton = useCallback(() => {
    const continueButton = document.getElementById('continueButton') || document.querySelector('.btn-primary');
    if (continueButton) {
      // Fast smooth scroll to button using custom animation
      const elementRect = continueButton.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const offset = window.innerHeight / 2 - elementRect.height / 2; // Center the element
      const targetPosition = absoluteElementTop - offset;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();
      const duration = 200; // Faster: 200ms instead of default ~500ms
      
      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth deceleration
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentPosition = startPosition + distance * easeOutCubic;
        
        window.scrollTo(0, currentPosition);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      
      requestAnimationFrame(animateScroll);
      
      // Add visual highlight to button
      continueButton.style.transition = 'all 0.2s ease';
      continueButton.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.3)';
      
      setTimeout(() => {
        continueButton.style.boxShadow = '';
      }, 2000);
    }
  }, []);

  // Mark field as actively being typed
  const markFieldAsActive = useCallback((fieldName) => {
    activeFields.current.add(fieldName);
    
    // Clear any existing scroll timer for this field
    if (scrollTimers.current[fieldName]) {
      clearTimeout(scrollTimers.current[fieldName]);
      delete scrollTimers.current[fieldName];
    }
    
    // Clear existing typing timer
    if (typingTimers.current[fieldName]) {
      clearTimeout(typingTimers.current[fieldName]);
    }
  }, []);

  // Mark field as inactive (typing stopped)
  const markFieldAsInactive = useCallback((fieldName) => {
    activeFields.current.delete(fieldName);
  }, []);

  // Check if field is still focused before scrolling
  const isFieldStillFocused = useCallback((fieldName) => {
    const fieldElement = document.getElementById(fieldName);
    return fieldElement && document.activeElement === fieldElement;
  }, []);

  // Simple auto-scroll logic with typing detection
  const handleAutoScroll = useCallback((currentField, isValid, triggerOnBlur = false) => {
    if (!isValid) return;

    const currentIndex = fieldOrder.indexOf(currentField);
    
    // Check if this is the last field and all fields are complete
    if (currentIndex === fieldOrder.length - 1) {
      // Update state first, then check if all fields are complete
      setFieldValidationStates(prev => {
        const updatedState = {
          ...prev,
          [currentField]: { ...prev[currentField], isValid, isComplete: true, hasScrolled: true }
        };
        
        // Check if all fields are now complete
        if (areAllFieldsComplete(updatedState)) {
          setTimeout(() => {
            scrollToContinueButton();
          }, 500);
        }
        
        return updatedState;
      });
      return;
    }
    
    if (currentIndex !== -1 && currentIndex < fieldOrder.length - 1) {
      const nextField = fieldOrder[currentIndex + 1];
      
      // Check if next field is visible (not conditional and hidden)
      const nextFieldElement = document.getElementById(nextField);
      if (nextFieldElement && nextFieldElement.offsetParent !== null) {
        
        // Mark current field as scrolled
        setFieldValidationStates(prev => ({
          ...prev,
          [currentField]: { ...prev[currentField], hasScrolled: true }
        }));
        
        // If triggered by blur, scroll immediately
        if (triggerOnBlur) {
          scrollToField(nextField);
          return;
        }
        
        // Check if user is still typing in this field
        if (activeFields.current.has(currentField)) {
          // User is still typing - don't scroll yet
          return;
        }
        
        // Check if field is still focused
        if (isFieldStillFocused(currentField)) {
          // Field is still focused - don't scroll yet
          return;
        }
        
        // Get adaptive delay for this field type
        const delay = getInactivityDelay(currentField);
        
        // Clear any existing scroll timer
        if (scrollTimers.current[currentField]) {
          clearTimeout(scrollTimers.current[currentField]);
        }
        
        // Set scroll timer with adaptive delay
        scrollTimers.current[currentField] = setTimeout(() => {
          // Double-check before scrolling
          if (!activeFields.current.has(currentField) && !isFieldStillFocused(currentField)) {
            scrollToField(nextField);
          }
          delete scrollTimers.current[currentField];
        }, delay);
      }
    }
  }, [fieldOrder, scrollToField, areAllFieldsComplete, scrollToContinueButton, getInactivityDelay, isFieldStillFocused]);

  // Update field validation state and trigger auto-scroll
  const updateFieldValidationState = useCallback((fieldName, value, triggerOnBlur = false) => {
    const isValid = isFieldValid(fieldName, value);
    const isComplete = value && value.toString().trim() !== '';
    
    // Mark field as active when typing
    if (value && value.toString().trim() !== '') {
      markFieldAsActive(fieldName);
      
      // Set timer to mark as inactive after typing stops
      const inactivityDelay = getInactivityDelay(fieldName);
      if (typingTimers.current[fieldName]) {
        clearTimeout(typingTimers.current[fieldName]);
      }
      
      typingTimers.current[fieldName] = setTimeout(() => {
        markFieldAsInactive(fieldName);
        
        // After inactivity, check if we should scroll
        setFieldValidationStates(prev => {
          const currentState = prev[fieldName] || { hasScrolled: false };
          if (isValid && !currentState.hasScrolled) {
            handleAutoScroll(fieldName, isValid, triggerOnBlur);
          }
          return prev;
        });
      }, inactivityDelay);
    } else {
      // Empty value - mark as inactive
      markFieldAsInactive(fieldName);
    }
    
    setFieldValidationStates(prev => {
      const currentState = prev[fieldName] || { hasScrolled: false };
      const hasScrolled = currentState.hasScrolled;
      
      // Trigger auto-scroll if field becomes valid and hasn't been scrolled yet
      // Only if not triggered by blur (blur will handle it separately)
      if (isValid && !hasScrolled && !triggerOnBlur) {
        // Use setTimeout to avoid state update conflicts
        setTimeout(() => {
          handleAutoScroll(fieldName, isValid, triggerOnBlur);
        }, 0);
      }
      
      return {
        ...prev,
        [fieldName]: { 
          ...currentState, 
          isValid, 
          isComplete,
          hasScrolled
        }
      };
    });
  }, [isFieldValid, handleAutoScroll, markFieldAsActive, markFieldAsInactive, getInactivityDelay]);
  
  // Handle blur event - trigger scroll check immediately
  const handleBlur = useCallback((fieldName, value) => {
    // Mark field as inactive
    markFieldAsInactive(fieldName);
    
    // Clear typing timer
    if (typingTimers.current[fieldName]) {
      clearTimeout(typingTimers.current[fieldName]);
      delete typingTimers.current[fieldName];
    }
    
    // Update validation state and trigger scroll check
    updateFieldValidationState(fieldName, value, true);
  }, [markFieldAsInactive, updateFieldValidationState]);

  // Enhanced input change handler
  const createInputChangeHandler = useCallback((setData) => (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Special handling for registration number
    if (name === 'registrationNumber') {
      processedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    }

    // Special handling for chassis number
    if (name === 'chassisNumber') {
      processedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    }

    // phoneNumber / idNumber: sanitization lives in InsuredDetails.enhancedInputChange
    // (must not strip here or a second setState overwrites e.g. BRN "C.19574" or +254 phones)

    setData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Update validation state and trigger auto-scroll
    updateFieldValidationState(name, processedValue);
  }, [updateFieldValidationState]);

  // Focus handler
  const handleFocus = useCallback((fieldName) => {
    setLastFocusedField(fieldName);
  }, []);

  // Scroll to top of viewport with faster animation
  const scrollToTop = useCallback(() => {
    const startPosition = window.pageYOffset || document.documentElement.scrollTop;
    const startTime = performance.now();
    const duration = 200; // Faster: 200ms instead of default ~500ms
    
    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth deceleration
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentPosition = startPosition * (1 - easeOutCubic);
      
      window.scrollTo(0, currentPosition);
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };
    
    requestAnimationFrame(animateScroll);
  }, []);

  return {
    fieldValidationStates,
    lastFocusedField,
    initializeFieldStates,
    updateFieldValidationState,
    createInputChangeHandler,
    handleFocus,
    handleBlur,
    markFieldAsActive,
    markFieldAsInactive,
    scrollToField,
    scrollToTop,
    scrollToContinueButton,
    areAllFieldsComplete
  };
};
